import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Edit2, Trash2, Share } from 'lucide-react';
import RichTextEditor from '@/components/ui/rich-text-editor';
import { useNotes, Note } from '@/hooks/useNotes';
import { useNoteAccess } from '@/hooks/useNoteAccess';
import { useBrandTeam } from '@/hooks/useBrandTeam';
import { TeamMemberSelector } from './TeamMemberSelector';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface NotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'my' | 'shared' | 'create';
  brandId: string;
  selectedNoteId?: string | null;
}

export const NotesDialog = ({ open, onOpenChange, type, brandId, selectedNoteId }: NotesDialogProps) => {
  const { myNotes, sharedNotes, createNote, updateNote, deleteNote } = useNotes(brandId);
  const { teamMembers } = useBrandTeam(brandId);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [memberRoles, setMemberRoles] = useState<{ [memberId: string]: string }>({});
  const [editSelectedTeamMembers, setEditSelectedTeamMembers] = useState<string[]>([]);
  const [editMemberRoles, setEditMemberRoles] = useState<{ [memberId: string]: string }>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  // Load existing shared users when editing a note
  useEffect(() => {
    const loadExistingSharedUsers = async () => {
      if (!editingNote) {
        setEditSelectedTeamMembers([]);
        setEditMemberRoles({});
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_notes_access')
          .select('user_id, role')
          .eq('note_id', editingNote.id);

        if (error) throw error;

        if (data && data.length > 0) {
          const userIds = data.map(item => item.user_id);
          const roles = data.reduce((acc, item) => {
            acc[item.user_id] = item.role;
            return acc;
          }, {} as { [userId: string]: string });

          setEditSelectedTeamMembers(userIds);
          setEditMemberRoles(roles);
        } else {
          setEditSelectedTeamMembers([]);
          setEditMemberRoles({});
        }
      } catch (error) {
        console.error('Error loading shared users:', error);
        setEditSelectedTeamMembers([]);
        setEditMemberRoles({});
      }
    };

    loadExistingSharedUsers();
  }, [editingNote]);

  // Auto-open specific note when selectedNoteId is provided
  useEffect(() => {
    if (selectedNoteId && (myNotes.length > 0 || sharedNotes.length > 0)) {
      const allNotes = [...myNotes, ...sharedNotes];
      const noteToEdit = allNotes.find(note => note.id === selectedNoteId);
      
      if (noteToEdit) {
        console.log('Auto-opening note for editing:', noteToEdit.title);
        setEditingNote(noteToEdit);
      }
    }
  }, [selectedNoteId, myNotes, sharedNotes]);

  const handleTeamSelectionChange = (memberIds: string[], roles: { [memberId: string]: string }) => {
    setSelectedTeamMembers(memberIds);
    setMemberRoles(roles);
  };

  const handleEditTeamSelectionChange = (memberIds: string[], roles: { [memberId: string]: string }) => {
    setEditSelectedTeamMembers(memberIds);
    setEditMemberRoles(roles);
  };

  const getDialogTitle = () => {
    switch (type) {
      case 'my':
        return 'My Notes';
      case 'shared':
        return 'Shared Notes';
      case 'create':
        return 'Create New Note';
      default:
        return 'Notes';
    }
  };

  const getPopupContext = () => {
    return `/notes-${type}`;
  };

  const handleCreateNote = async () => {
    if (!newNoteTitle.trim()) {
      toast.error('Please enter a note title');
      return;
    }

    const result = await createNote(newNoteTitle, newNoteContent, selectedTeamMembers);
    if (result) {
      toast.success('Note created successfully');
      setNewNoteTitle('');
      setNewNoteContent('');
      setSelectedTeamMembers([]);
      onOpenChange(false);
    } else {
      toast.error('Failed to create note');
    }
  };

  const handleUpdateNote = async (note: Note) => {
    const result = await updateNote(note.id, {
      title: note.title,
      content: note.content,
      is_shared: editSelectedTeamMembers.length > 0,
    }, editSelectedTeamMembers);
    if (result) {
      toast.success('Note updated successfully');
      setEditingNote(null);
      setEditSelectedTeamMembers([]);
      onOpenChange(false); // Close the popup
    } else {
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    setNoteToDelete(noteId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteNote = async () => {
    const idToDelete = noteToDelete || editingNote?.id;
    if (!idToDelete) return;
    
    const result = await deleteNote(idToDelete);
    if (result) {
      toast.success('Note deleted successfully');
      setEditingNote(null);
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
    } else {
      toast.error('Failed to delete note');
    }
    setDeleteConfirmOpen(false);
    setNoteToDelete(null);
  };

  const handleShareWithAllTeam = async (note: Note) => {
    // Share with all team members as viewers by default
    try {
      // Get all team member IDs (excluding the current user)
      const allTeamMemberIds = teamMembers
        .map(member => member.user_id)
        .filter(userId => userId !== note.user_id); // Don't share with the note owner

      const result = await updateNote(note.id, {
        is_shared: true,
      }, allTeamMemberIds);
      
      if (result) {
        toast.success(`Note shared with ${allTeamMemberIds.length} team members`);
      } else {
        toast.error('Failed to share note');
      }
    } catch (error) {
      toast.error('Failed to share note');
    }
  };

  const handleToggleShare = async (note: Note) => {
    if (!note.is_shared) {
      // If not shared, share with all team
      await handleShareWithAllTeam(note);
    } else {
      // If shared, unshare
      const result = await updateNote(note.id, {
        is_shared: false,
      }, []);
      
      if (result) {
        toast.success('Note unshared');
      } else {
        toast.error('Failed to update sharing status');
      }
    }
  };

  const renderNotesList = (notes: Note[], showShareToggle: boolean = false) => (
    <div className="space-y-3">
      {notes.length === 0 ? (
        <p className="text-muted-foreground text-center py-8">No notes found</p>
      ) : (
        notes.map((note) => (
          <div key={note.id} className="border rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{note.title}</h3>
              <div className="flex items-center gap-1">
                {showShareToggle && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleShare(note)}
                    className="h-8 w-8 p-0"
                  >
                    <Share className={`h-4 w-4 ${note.is_shared ? 'text-primary' : 'text-muted-foreground'}`} />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingNote(note)}
                  className="h-8 w-8 p-0"
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                {showShareToggle && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteNote(note.id)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div 
              className="text-sm text-muted-foreground prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: note.content || 'No content' }}
            />
            <div className="text-xs text-muted-foreground">
              Updated: {new Date(note.updated_at).toLocaleDateString()}
              {note.is_shared && <span className="ml-2 text-primary">• Shared</span>}
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderCreateForm = () => (
    <div className="space-y-6 px-4">
      <div>
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={newNoteTitle}
          onChange={(e) => setNewNoteTitle(e.target.value)}
          placeholder="Enter note title..."
        />
      </div>
      
      <div>
        <Label htmlFor="content">Content</Label>
        <RichTextEditor
          value={newNoteContent}
          onChange={setNewNoteContent}
          placeholder="Write your note content..."
          className="min-h-[200px] mt-2"
        />
      </div>

      <div>
        <Label className="text-sm font-medium">Share with team members</Label>
        <div className="mt-2 border rounded-lg p-3">
          <TeamMemberSelector
            brandId={brandId}
            selectedMembers={selectedTeamMembers}
            onSelectionChange={handleTeamSelectionChange}
            showShareWithAll={true}
            onShareWithAll={() => {
              // This will be handled by the selector itself
            }}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={handleCreateNote}>
          <Plus className="w-4 h-4 mr-2" />
          Create Note
        </Button>
      </div>
    </div>
  );

  const renderEditForm = () => {
    if (!editingNote) return null;

    return (
      <div className="space-y-6 px-4">
        <div>
          <Label htmlFor="edit-title">Title</Label>
          <Input
            id="edit-title"
            value={editingNote.title}
            onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
          />
        </div>
        
        <div>
          <Label htmlFor="edit-content">Content</Label>
          <RichTextEditor
            value={editingNote.content}
            onChange={(content) => setEditingNote({ ...editingNote, content })}
            className="min-h-[200px] mt-2"
          />
        </div>

        <div>
          <Label className="text-sm font-medium">Share with team members</Label>
          <div className="mt-2 border rounded-lg p-3">
            <TeamMemberSelector
              brandId={brandId}
              selectedMembers={editSelectedTeamMembers}
              onSelectionChange={handleEditTeamSelectionChange}
            />
          </div>
        </div>

        <div className="flex justify-between pt-4">
          {editingNote.is_shared || editSelectedTeamMembers.length > 0 ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled
                    className="text-destructive hover:text-destructive disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Unshare the note first to delete it</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
                  confirmDeleteNote();
                }
              }}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setEditingNote(null)}>
              Cancel
            </Button>
            <Button onClick={() => handleUpdateNote(editingNote)}>
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] mx-4">
        <DialogHeader>
          <DialogTitle>
            {getDialogTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto px-2">
          {editingNote ? (
            renderEditForm()
          ) : type === 'create' ? (
            renderCreateForm()
          ) : type === 'my' ? (
            renderNotesList(myNotes, true)
          ) : (
            renderNotesList(sharedNotes, false)
          )}
        </div>
        </DialogContent>
      </Dialog>

    </>
  );
};